import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  User as FirebaseUser 
} from 'firebase/auth';
import { auth, googleProvider, API_BASE_URL } from '../services/firebase';

// 인증 컨텍스트 타입 정의
interface AuthContextType {
  user: FirebaseUser | null;           // Firebase 사용자 정보
  userRole: string | null;             // 사용자 권한 ('developer' | 'admin' | 'viewer')
  loading: boolean;                    // 로딩 상태
  signIn: () => Promise<void>;         // 로그인 함수
  logout: () => Promise<void>;         // 로그아웃 함수
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 커스텀 훅: 다른 컴포넌트에서 인증 정보 사용
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth는 AuthProvider 내부에서만 사용 가능합니다');
  }
  return context;
};

// 인증 제공자 컴포넌트
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Firebase 인증 상태 변경 감지
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      
      if (firebaseUser?.email) {
        // Google Sheets에서 사용자 권한 확인
        try {
          console.log('사용자 권한 확인 중:', firebaseUser.email);
          
          const response = await fetch(
            `${API_BASE_URL}?action=getTrainingData&email=${encodeURIComponent(firebaseUser.email)}`
          );
          const data = await response.json();
          
          if (data.status === 'success' && data.userRole) {
            console.log('권한 확인 성공:', data.userRole);
            setUserRole(data.userRole);
          } else {
            console.warn('권한이 없는 사용자:', data.message);
            setUserRole(null);
          }
        } catch (error) {
          console.error('권한 확인 실패:', error);
          setUserRole(null);
        }
      } else {
        setUserRole(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Google 로그인 함수
  const signIn = async () => {
    try {
      console.log('Google 로그인 시도 중...');
      await signInWithPopup(auth, googleProvider);
    } catch (error: any) {
      // 사용자가 팝업을 취소한 경우는 에러로 처리하지 않음
      if (error.code !== 'auth/cancelled-popup-request' && 
          error.code !== 'auth/popup-closed-by-user') {
        console.error('로그인 실패:', error);
        alert('로그인 실패: ' + error.message);
      }
    }
  };

  // 로그아웃 함수
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserRole(null);
      console.log('로그아웃 완료');
    } catch (error) {
      console.error('로그아웃 실패:', error);
      alert('로그아웃 중 오류가 발생했습니다.');
    }
  };

  const value = {
    user,
    userRole,
    loading,
    signIn,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
